import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export interface OnlyPromoteReferrer {
    id: number;
    onlyfansId: number;
    name: string;
    clickCount: number;
    createdAt: string;
    updatedAt: string;
}

const QUERY_KEY = 'onlyPromoteReferrers';

const useOnlyPromoteReferrers = () => {
    const settingsContext = useContext(SettingsContext);
    const userContext = useContext(UserContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const queryClient = useQueryClient();

    const baseUrl = `${settingsContext.routes.menu.base}${params.userId}/referrers`;

    const getReferrers = async (): Promise<OnlyPromoteReferrer[]> => {
        return axios
            .get(baseUrl)
            .then((response) => response.data)
            .catch((error) => {
                handleHttpError(error, dialog);
                return [];
            });
    };

    const referrersQuery = useQuery([QUERY_KEY, params.userId], getReferrers, {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
    });

    const createMutation = useMutation(
        async (name: string) => {
            const response = await axios.post(baseUrl, { name });
            return response.data as OnlyPromoteReferrer;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEY, params.userId]);
            },
            onError: (error) => {
                handleHttpError(error, dialog);
            },
        },
    );

    const updateMutation = useMutation(
        async ({ referrerId, name }: { referrerId: number; name: string }) => {
            const response = await axios.put(`${baseUrl}/${referrerId}`, { name });
            return response.data as OnlyPromoteReferrer;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEY, params.userId]);
            },
            onError: (error) => {
                handleHttpError(error, dialog);
            },
        },
    );

    const deleteMutation = useMutation(
        async (referrerId: number) => {
            await axios.delete(`${baseUrl}/${referrerId}`);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEY, params.userId]);
            },
            onError: (error) => {
                handleHttpError(error, dialog);
            },
        },
    );

    return {
        referrersQuery,
        createReferrer: createMutation.mutateAsync,
        isCreateReferrerLoading: createMutation.isLoading,
        updateReferrer: updateMutation.mutateAsync,
        isUpdateReferrerLoading: updateMutation.isLoading,
        deleteReferrer: deleteMutation.mutateAsync,
        isDeleteReferrerLoading: deleteMutation.isLoading,
    };
};

export default useOnlyPromoteReferrers;
